import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiErrorResponse } from "../models/ApiResponse";
import { CreateCandidateRequest, MessageTemplate } from "../models/Candidate";
import Consultant, { Recruitment, UpdateConsultantRequest } from "../models/Consultant";
import { History } from "../models/RecruitmentHistory";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";

export const queryClient = new QueryClient();

type QueryOpts = {
	enabled?: boolean;
	refetchInterval?: number;
};

export function useCandidate(candidateId: string, opts: QueryOpts = {}) {
	const { data, ...rest } = useQuery(
		["candidates", candidateId],
		() =>
			apiStore
				.get<Consultant>({
					path: "/candidate-account/" + candidateId,
					isAuthRequired: true,
					query: { expand: "manager" },
				})
				.then((d) => d.data),
		{
			...opts,
		}
	);

	return {
		candidate: data,
		...rest,
	};
}

export type GetCandidatesParams = {
	page: number;
	pageSize: number;
	status?: string[];
	keyword?: string;
	currentAction?: string;
	nextAction?: string;
	sort?: string;
	order?: string;
	pool?: string;
};

export function useCandidates(params: GetCandidatesParams, opts: QueryOpts = {}) {
	const { status, ...query } = params;

	const { data, ...rest } = useQuery(
		["candidates", "list", params],
		() =>
			apiStore
				.get<{ totalCount: number; users: Consultant[] }>({
					path: "/candidate-account",
					isAuthRequired: true,
					query: {
						status: status ? status.join(",") : undefined,
						...query,
					},
				})
				.then((d) => d.data),
		{
			...opts,
			keepPreviousData: true,
		}
	);

	return {
		candidates: data?.users,
		totalCount: data?.totalCount,
		...rest,
	};
}

export function useRecruitmentHistory({ userId }: { userId: string }, opts: QueryOpts = {}) {
	const { data, ...rest } = useQuery(
		["candidates", userId, "history"],
		() =>
			apiStore
				.get<History[]>({
					path: `/candidate-account/${userId}/history`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			...opts,
		}
	);

	return {
		history: data,
		...rest,
	};
}

export function useUpdateCandidate() {
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{ userId: string; silent?: boolean; update: Partial<UpdateConsultantRequest> }
	>(
		(params) => {
			if (params.update.profile?.competences) {
				params.update.profile.competences = params.update.profile.competences.map((c) => ({
					...c,
					skills: c.skills && c.skills.length === 0 ? undefined : c.skills,
				}));
			}
			return apiStore
				.put({
					path: `/candidate-account/${params.userId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data);
		},
		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.successGeneric();
				}
				queryClient.invalidateQueries(["candidates", data.userId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useUpdateCandidateRecruitmentDetails() {
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{ userId: string; silent?: boolean; update: Partial<Recruitment> }
	>(
		(params) =>
			apiStore
				.put({
					path: `/candidate-account/${params.userId}/recruitment`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.successGeneric();
				}
				queryClient.invalidateQueries(["candidates", data.userId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useRecruitCandidate() {
	const queryClient = useQueryClient();

	return useMutation<any, ApiErrorResponse, { userId: string; silent?: boolean }>(
		(params) =>
			apiStore
				.put({
					path: `/candidate-account/${params.userId}/recruit`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.successGeneric();
				}
				queryClient.invalidateQueries(["candidates", data.userId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useSendRecruitmentContract() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{ userId: string; sms: boolean; mail: boolean; silent?: boolean; expireDate?: string }
	>(
		(params) =>
			apiStore
				.post({
					path: `/candidate-account/${params.userId}/send-contract`,
					isAuthRequired: true,
					body: {
						channels: {
							mail: true,
							sms: params.sms,
						},
						expireDate: params.expireDate,
					},
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.success(t("recruitment.sendContractModal.success"));
				}
				queryClient.invalidateQueries(["candidates", data.userId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useSendCandidateMessage() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{ userId: string; message: string; subject: string; silent?: boolean }
	>(
		(params) =>
			apiStore
				.post({
					path: `/candidate-account/${params.userId}/message`,
					isAuthRequired: true,
					body: {
						message: params.message,
						subject: params.subject,
					},
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.success(t("recruitment.sendMessage.success"));
				}
				queryClient.invalidateQueries(["candidates", data.userId]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useBulkSendCandidateMessage() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{ userIds: string[]; message: string; subject: string; silent?: boolean }
	>(
		(params) =>
			apiStore
				.post({
					path: `/candidate-account/bulk/message`,
					isAuthRequired: true,
					body: {
						userIds: params.userIds,
						message: params.message,
						subject: params.subject,
					},
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.success(t("recruitment.sendMessage.success"));
				}
				queryClient.invalidateQueries(["candidates", "list"]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useBulkUpdateCandidateRecruitmentDetails() {
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{ userIds: string[]; silent?: boolean; update: Partial<Recruitment> }
	>(
		(params) =>
			apiStore
				.put({
					path: `/candidate-account/bulk/recruitment`,
					body: {
						userIds: params.userIds,
						update: params.update,
					},
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, data) => {
				if (!data.silent) {
					toast.successGeneric();
				}
				queryClient.invalidateQueries(["candidates", "list"]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useCreateCandidate() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	return useMutation<any, ApiErrorResponse, CreateCandidateRequest>(
		(params) =>
			apiStore
				.post({
					path: `/candidate-account`,
					isAuthRequired: true,
					body: params,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				toast.success(t("recruitment.created"));
				queryClient.refetchQueries(["candidates", "list"]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

export function useDeleteCandidate() {
	const queryClient = useQueryClient();

	return useMutation<any, ApiErrorResponse, { userId: string }>(
		(params) =>
			apiStore
				.delete({
					path: `/user/${params.userId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				toast.successGeneric();
				queryClient.invalidateQueries(["candidates", params.userId]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

export function useRecallAgreement() {
	const queryClient = useQueryClient();

	return useMutation<any, ApiErrorResponse, { userId: string }>(
		(params) =>
			apiStore
				.delete({
					path: `/candidate-account/${params.userId}/contract`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				toast.successGeneric();
				queryClient.invalidateQueries(["candidates", params.userId]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}
